import React, { useEffect, useState } from 'react';
import { Button, MessageBarActions, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { GuideDrawer, GuideDrawerForm } from '../../../../theme/components/GuideDrawer';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';
import { GuideBannerAction } from '../../../../theme/components/GuideButtons';
import { GuideEmptyState } from '../../../../theme/components/GuideEmptyState';
import { GuideSkeleton } from '../../../../theme/components/GuideSkeleton';
import { GuideTable, GuideTh, guideCellClassName as cell, guideRowClassName } from '../../../../theme/components/GuideTable';
import { fmtInvoiceDate, useInvoicesText } from '../../../CashFlow/Payments/PaymentInvoices/PaymentInvoices.Bits';
import { CreditorInvoiceFormDrawer } from './CreditorInvoice.FormDrawer';
import { settlementLabel } from '../PractitionerAccountStatement.Functions';
import { CreditorInvoiceData, SettlementInvoiceCandidate } from '../../../../models/Entities/CashFlow/CreditorInvoice/CreditorInvoice';
import { Settlement } from '../../../../models/Entities/Settlements/Settlement';
import { ErrorType } from '../../../../models/HttpError';

export interface SettlementInvoicesDrawerProps {
    open: boolean;
    onDismiss: () => void;
    practitionerName: string;
    settlements: Settlement[];
    currencies: string[];
    GetCandidates: (settlementIds: number[]) => Promise<{ candidates: SettlementInvoiceCandidate[]; failedSettlementIds: number[] }>;
    onRegister: (candidate: SettlementInvoiceCandidate, data: CreditorInvoiceData) => Promise<unknown>;
}

type LoadState = 'idle' | 'loading' | 'loaded' | 'failed';

/**
 * "Traer facturas de liquidaciones": lista las facturas subidas en el detalle de las liquidaciones del
 * profesional que todavía no son facturas del estado de cuenta, y permite registrarlas (source 2)
 * completando número, fecha e importe.
 */
export const SettlementInvoicesDrawer = (props: SettlementInvoicesDrawerProps) => {
    const tr = useInvoicesText();
    const [state, setState] = useState<LoadState>('idle');
    const [error, setError] = useState<ErrorType | undefined>(undefined);
    const [candidates, setCandidates] = useState<SettlementInvoiceCandidate[]>([]);
    const [failed, setFailed] = useState<number[]>([]);
    const [editing, setEditing] = useState<SettlementInvoiceCandidate | undefined>(undefined);
    const [registered, setRegistered] = useState<string | undefined>(undefined);

    const settlementIds = props.settlements.map((item) => item.id);

    const load = async () => {
        if (settlementIds.length === 0) {
            setCandidates([]);
            setState('loaded');
            return;
        }
        setState('loading');
        setError(undefined);
        try {
            const result = await props.GetCandidates(settlementIds);
            setCandidates(result.candidates);
            setFailed(result.failedSettlementIds);
            setState('loaded');
        } catch (caught: unknown) {
            setError(caught as ErrorType);
            setState('failed');
        }
    };

    useEffect(() => {
        if (!props.open) return;
        setEditing(undefined);
        setRegistered(undefined);
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open]);

    const renderList = () => {
        if (state === 'failed') {
            return (
                <GuideMessageBar intent="error" layout="multiline">
                    <MessageBarBody>
                        <MessageBarTitle>{tr('settlementFiles.error')}</MessageBarTitle>
                        {error?.ErrorMessage}
                    </MessageBarBody>
                    <MessageBarActions><GuideBannerAction onClick={load}>{tr('actions.retry')}</GuideBannerAction></MessageBarActions>
                </GuideMessageBar>
            );
        }
        if (state !== 'loaded') return <GuideSkeleton rows={3} label={tr('picker.loading')} data-testid="settlement-invoices-loading" />;
        if (settlementIds.length === 0) {
            return <GuideEmptyState title={tr('settlementFiles.noSettlementsTitle')} text={tr('settlementFiles.noSettlementsText')} />;
        }
        return (
            <>
                {failed.length > 0 && (
                    <GuideMessageBar intent="warning" layout="multiline" data-testid="settlement-invoices-partial">
                        <MessageBarBody>{tr('settlementFiles.partialError', { list: failed.map((id) => settlementLabel(id, props.settlements)).join(', ') })}</MessageBarBody>
                        <MessageBarActions><GuideBannerAction onClick={load}>{tr('actions.retry')}</GuideBannerAction></MessageBarActions>
                    </GuideMessageBar>
                )}
                {candidates.length === 0 ? (
                    <GuideEmptyState title={tr('settlementFiles.emptyTitle')} text={tr('settlementFiles.emptyText')} data-testid="settlement-invoices-empty" />
                ) : (
                    <GuideTable ariaLabel={tr('settlementFiles.title')}>
                        <thead>
                            <tr>
                                <GuideTh>{tr('settlementFiles.columns.file')}</GuideTh>
                                <GuideTh>{tr('settlementFiles.columns.settlement')}</GuideTh>
                                <GuideTh>{tr('settlementFiles.columns.uploaded')}</GuideTh>
                                <th className={cell('end')} scope="col" />
                            </tr>
                        </thead>
                        <tbody>
                            {candidates.map((candidate) => (
                                <tr key={candidate.path} className={guideRowClassName()} data-testid={`settlement-invoice-${candidate.settlementFileId}`}>
                                    <td className="pinv-strong">{candidate.fileName}</td>
                                    <td>{`LIQ-${candidate.settlementId} · ${settlementLabel(candidate.settlementId, props.settlements)}`}</td>
                                    <td className="ichtys-num pinv-secondary-cell">{fmtInvoiceDate(candidate.uploadedAt)}</td>
                                    <td className={cell('end')}>
                                        <Button appearance="secondary" size="small" onClick={() => { setRegistered(undefined); setEditing(candidate); }}>
                                            {tr('settlementFiles.complete')}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </GuideTable>
                )}
            </>
        );
    };

    return (
        <>
            <GuideDrawer
                open={props.open && !editing}
                size="large"
                title={tr('settlementFiles.title')}
                eyebrow={tr('form.eyebrow', { name: props.practitionerName })}
                subtitle={tr('settlementFiles.description', { name: props.practitionerName })}
                onDismiss={props.onDismiss}
                closeLabel={tr('settlementFiles.close')}
                testId="settlement-invoices-drawer"
                secondaryAction={{ label: tr('settlementFiles.close') }}
            >
                <GuideDrawerForm testId="settlement-invoices">
                    {registered && (
                        <GuideMessageBar intent="success" data-testid="settlement-invoices-registered">
                            <MessageBarBody>{tr('settlementFiles.registered', { name: registered })}</MessageBarBody>
                        </GuideMessageBar>
                    )}
                    {renderList()}
                </GuideDrawerForm>
            </GuideDrawer>
            <CreditorInvoiceFormDrawer
                open={props.open && !!editing}
                mode="settlement"
                candidate={editing}
                practitionerName={props.practitionerName}
                currencies={props.currencies}
                settlements={props.settlements}
                secondaryLabel={tr('settlementFiles.back')}
                onDismiss={() => setEditing(undefined)}
                onUpload={() => Promise.reject(new Error('not used'))}
                onRetryRegister={() => Promise.resolve()}
                onUpdate={() => Promise.resolve()}
                onRegisterSettlement={props.onRegister}
                onDone={() => {
                    if (editing) {
                        setRegistered(editing.fileName);
                        setCandidates((current) => current.filter((item) => item.path !== editing.path));
                    }
                    setEditing(undefined);
                }}
            />
        </>
    );
};

export default SettlementInvoicesDrawer;
